<template>
  <div class="landing-wrapper">
    <div class="noise-bg"></div>

    <!-- BARRA DE NAVEGAÇÃO -->
    <nav class="navbar" :class="{ 'navbar-scrolled': scrolled }">
      <div class="navbar-inner">
        <a href="#" class="logo">
          <div class="logo-icon">nd</div>
          <span class="logo-text">new<span>dental</span></span>
        </a>
        <ul class="nav-links">
          <li><a href="#sobre">Sobre</a></li>
          <li><a href="#funcionalidades">Funcionalidades</a></li>
          <li><a href="#screenshots">Aplicação</a></li>
          <li><a href="#planos">Planos</a></li>
        </ul>
        <a href="#planos" class="nav-cta">Começar Agora</a>
      </div>
    </nav>

    <!-- HERO -->
    <section class="hero" id="inicio">
      <div class="hero-bg-orb orb1"></div>
      <div class="hero-bg-orb orb2"></div>
      <div class="hero-bg-orb orb3"></div>
      
      <v-container class="position-relative z-index-1" style="max-width: 1200px;">
        <v-row align="center" justify="space-between">
          <v-col cols="12" md="6" class="hero-content">
            <div class="hero-badge">
              <div class="hero-badge-dot"></div>
              Gestão Odontológica
            </div>
            <h1 class="hero-title">
              O seu consultório<br/>
              na palma da<br/>
              <span class="gradient-text">sua mão</span>
            </h1>
            <p class="hero-subtitle">
              O New Dental centraliza tudo o que precisa para gerir a sua clínica: pacientes, agendamentos, odontograma, orçamentos, anamnese e muito mais.
            </p>
            <div class="hero-actions">
              <a href="#planos" class="btn-primary">▶ Experimente Grátis</a>
              <a href="#screenshots" class="btn-secondary">Ver a Aplicação</a>
            </div>
            <div class="hero-stats">
              <div class="hero-stat-item">
                <span class="hero-stat-number">+5.000</span>
                <span class="hero-stat-label">Dentistas ativos</span>
              </div>
              <div class="hero-stat-item">
                <span class="hero-stat-number">+120k</span>
                <span class="hero-stat-label">Pacientes registados</span>
              </div>
              <div class="hero-stat-item">
                <span class="hero-stat-number">4.9★</span>
                <span class="hero-stat-label">Avaliação nas lojas</span>
              </div>
            </div>
          </v-col>

          <!-- VISUAL HERO (Mockup & Cartões) -->
          <v-col cols="12" md="6" class="d-none d-md-flex justify-center position-relative">
            <div class="hero-visual">
              
              <div class="phone-mockup">
                <div class="phone-notch"></div>
                <div class="phone-screen">
                  <div class="phone-header-bar">
                    <div>
                      <div class="phone-header-logo">newdental</div>
                      <div class="d-flex align-center" style="gap:8px;">
                        <div class="phone-avatar"></div>
                        <div>
                          <div class="phone-user-name">Alex Tubino</div>
                          <div class="phone-user-sub">11 consultas às 9h</div>
                        </div>
                      </div>
                    </div>
                    <div class="screen-logo-small">nd</div>
                  </div>
                  
                  <div class="phone-grid">
                    <div class="phone-card" v-for="item in ['👥 Colaboradores', '❤️ Pacientes', '📋 Consultas', '🦷 DSD', '💲 Orçamentos', '🗓️ Agendamento']" :key="item">
                      <div class="phone-card-icon">{{ item.split(' ')[0] }}</div>
                      <div class="phone-card-label">{{ item.split(' ')[1] }}</div>
                    </div>
                  </div>
                  
                  <div class="phone-appt">
                    <div>
                      <div class="phone-appt-name">Vinicius Ribeiro</div>
                      <div class="phone-appt-time">09:00 • Limpeza</div>
                    </div>
                    <div class="phone-appt-dot"></div>
                  </div>
                  <div class="phone-appt">
                    <div>
                      <div class="phone-appt-name">Carlos Eduardo</div>
                      <div class="phone-appt-time">10:30 • Canal</div>
                    </div>
                    <div class="phone-appt-dot" style="background:#00c878;"></div>
                  </div>
                  
                  <div class="phone-nav">
                    <div class="phone-nav-item active">🏠</div>
                    <div class="phone-nav-item">🔔</div>
                    <div class="phone-nav-item">📷</div>
                    <div class="phone-nav-item">nd</div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- FUNCIONALIDADES / SOBRE -->
    <section class="features-section" id="funcionalidades">
      <v-container style="max-width: 1200px;">
        <div class="features-header reveal">
          <div class="section-badge">Funcionalidades</div>
          <h2 class="section-title">Tudo num só lugar<br/><span class="gradient-text">para a sua clínica</span></h2>
          <p class="features-subtitle">Do registo do paciente até ao fecho do orçamento, o New Dental cobre cada etapa da sua rotina odontológica.</p>
        </div>
        
        <v-row>
          <v-col cols="12" sm="6" md="4" v-for="(feature, index) in features" :key="index" class="d-flex">
            <v-card class="feature-card reveal flex-grow-1" :class="`reveal-delay-${(index % 3) + 1}`" flat>
              <v-card-text class="pa-0">
                <div class="feature-icon">{{ feature.icon }}</div>
                <div class="feature-title">{{ feature.title }}</div>
                <p class="feature-desc mb-0">{{ feature.desc }}</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- MARKETING / MÉTRICAS -->
    <section class="marketing-section" id="sobre">
      <v-container style="max-width: 1200px;">
        <v-row align="center">
          <v-col cols="12" md="6" class="marketing-visual reveal">
            <div class="mkt-card-stack">
              <div class="mkt-card mkt-card-back2"></div>
              <div class="mkt-card mkt-card-back1"></div>
              <div class="mkt-card mkt-card-main">
                <div class="d-flex justify-space-between align-center mb-4">
                  <div>
                    <div style="font-size:11px;color:rgba(255,255,255,0.4);font-weight:600;text-transform:uppercase;letter-spacing:0.8px;">Consultas — Nov 2024</div>
                    <div style="font-family:'Syne',sans-serif;font-size:28px;font-weight:800;margin-top:4px;">218 realizadas</div>
                  </div>
                  <div style="background:rgba(0,200,120,0.15);border:1px solid rgba(0,200,120,0.3);padding:6px 12px;border-radius:100px;font-size:12px;font-weight:700;color:#00c878;">↑ 18%</div>
                </div>
                <div class="mkt-chart-bar">
                  <div class="bar" style="height:35%"></div>
                  <div class="bar" style="height:55%"></div>
                  <div class="bar" style="height:45%"></div>
                  <div class="bar" style="height:70%"></div>
                  <div class="bar active" style="height:90%"></div>
                  <div class="bar" style="height:60%"></div>
                  <div class="bar" style="height:75%"></div>
                  <div class="bar" style="height:85%"></div>
                  <div class="bar" style="height:65%"></div>
                  <div class="bar" style="height:80%"></div>
                  <div class="bar" style="height:95%"></div>
                  <div class="bar" style="height:100%"></div>
                </div>
                <div class="d-flex justify-space-between mt-4">
                  <div>
                    <div style="font-size:10px;color:rgba(255,255,255,0.4);">Novos Pacientes</div>
                    <div style="font-family:'Syne',sans-serif;font-size:20px;font-weight:800;">+47</div>
                  </div>
                  <div>
                    <div style="font-size:10px;color:rgba(255,255,255,0.4);">Receita gerada</div>
                    <div style="font-family:'Syne',sans-serif;font-size:20px;font-weight:800;">R$ 32k</div>
                  </div>
                  <div>
                    <div style="font-size:10px;color:rgba(255,255,255,0.4);">Taxa de presença</div>
                    <div style="font-family:'Syne',sans-serif;font-size:20px;font-weight:800;">96%</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="mkt-big-number-wrapper reveal reveal-delay-2">
              <div class="mkt-big-number">+5k</div>
              <div class="mkt-big-label">Dentistas Ativos</div>
            </div>
          </v-col>
          
          <v-col cols="12" md="6" class="mkt-content reveal reveal-delay-2 pl-md-12">
            <div class="section-badge">Porquê o New Dental?</div>
            <h2 class="section-title mb-5">Menos papel.<br/>Mais <span class="gradient-text">resultado.</span></h2>
            <p>Chega de folhas de cálculo, cadernos e sistemas lentos. O New Dental foi construído por dentistas, para dentistas — com uma interface moderna que aprende a usar em minutos.</p>
            <p>Acompanhe a evolução de cada paciente, emita orçamentos profissionais e gira toda a equipa numa única aplicação — no telemóvel ou no tablet.</p>
            
            <ul class="mkt-list">
              <li v-for="(item, i) in marketingList" :key="i">{{ item }}</li>
            </ul>
            
            <div class="mkt-tag-row">
              <span class="mkt-tag" v-for="tag in marketingTags" :key="tag">{{ tag }}</span>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- SCREENSHOTS / APLICAÇÃO -->
    <section class="screenshots-section" id="screenshots">
      <div class="screenshots-header reveal">
        <div class="section-badge">Capturas da Aplicação</div>
        <h2 class="section-title">Veja como é <span class="gradient-text">por dentro</span></h2>
        <p style="color:rgba(255,255,255,0.55);font-size:16px;margin-top:14px;">Interface limpa e intuitiva, desenhada para o dia a dia do consultório.</p>
      </div>
      
      <!-- Mantendo as secções de interface nativa em HTML por questões de especificidade CSS -->
      <div class="screens-track">
        <!-- Ecrã 1: Home -->
        <div class="screen-item">
          <div class="screen-header-bar">
            <span class="screen-header-title">newdental</span>
            <div class="screen-logo-small">nd</div>
          </div>
          <div class="screen-body">
            <div class="screen-label">Bem-vindo</div>
            <div style="background:rgba(142,68,173,0.15);border-radius:10px;padding:10px;margin-bottom:10px;text-align:center;">
              <div style="font-size:10px;font-weight:700;color:#fff;">Dr. Alex Tubino</div>
              <div style="font-size:8px;color:rgba(255,255,255,0.5);">11 consultas</div>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:5px;">
              <div class="phone-card"><div class="phone-card-icon">👥</div><div class="phone-card-label">Colab.</div></div>
              <div class="phone-card"><div class="phone-card-icon">❤️</div><div class="phone-card-label">Pacientes</div></div>
              <div class="phone-card"><div class="phone-card-icon">📋</div><div class="phone-card-label">Consultas</div></div>
              <div class="phone-card"><div class="phone-card-icon">💲</div><div class="phone-card-label">Orçamentos</div></div>
            </div>
          </div>
          <div class="screen-caption">Ecrã Inicial</div>
        </div>

        <!-- Ecrã 2: Consultas -->
        <div class="screen-item">
          <div class="screen-header-bar">
            <span class="screen-header-title">Consultas 2024</span>
            <div class="screen-logo-small">nd</div>
          </div>
          <div class="screen-body">
            <div class="tab-row">
              <div class="tab-btn active">Semana</div>
              <div class="tab-btn">Mês</div>
            </div>
            <div class="screen-label">2/4 HOJE</div>
            <div class="screen-list-item">
              <div>
                <div class="screen-item-name">Vinícius Ribeiro</div>
                <div class="screen-item-sub">09:00 • Limpeza</div>
              </div>
              <div class="screen-item-badge">✏️</div>
            </div>
            <div class="screen-list-item">
              <div>
                <div class="screen-item-name">Carlos Eduardo</div>
                <div class="screen-item-sub">10:30 • Canal</div>
              </div>
              <div class="screen-item-badge">✏️</div>
            </div>
            <div style="background:linear-gradient(135deg,var(--primary),var(--quaternary));border-radius:10px;padding:8px;text-align:center;margin-top:8px;">
              <div style="font-size:9px;font-weight:700;color:#fff;">+ Nova consulta</div>
            </div>
          </div>
          <div class="screen-caption">Agenda</div>
        </div>

        <!-- Ecrã 3: Calendário -->
        <div class="screen-item">
          <div class="screen-header-bar">
            <span class="screen-header-title">Consultas 2024</span>
            <div class="screen-logo-small">nd</div>
          </div>
          <div class="screen-body">
            <div class="tab-row">
              <div class="tab-btn">Semana</div>
              <div class="tab-btn active">Mês</div>
            </div>
            <div class="screen-label" style="text-align:center;color:#c77dff;">JAN</div>
            <div class="screen-calendar-grid">
              <div class="cal-cell" style="font-size:6px;color:rgba(255,255,255,0.25);">S</div>
              <div class="cal-cell" style="font-size:6px;color:rgba(255,255,255,0.25);">T</div>
              <div class="cal-cell" style="font-size:6px;color:rgba(255,255,255,0.25);">Q</div>
              <div class="cal-cell" style="font-size:6px;color:rgba(255,255,255,0.25);">Q</div>
              <div class="cal-cell" style="font-size:6px;color:rgba(255,255,255,0.25);">S</div>
              <div class="cal-cell" style="font-size:6px;color:rgba(255,255,255,0.25);">S</div>
              <div class="cal-cell" style="font-size:6px;color:rgba(255,255,255,0.25);">D</div>
              <div class="cal-cell">1</div><div class="cal-cell has-appt">2</div><div class="cal-cell">3</div>
              <div class="cal-cell has-appt">4</div><div class="cal-cell">5</div><div class="cal-cell">6</div><div class="cal-cell">7</div>
              <div class="cal-cell">8</div><div class="cal-cell today">9</div><div class="cal-cell">10</div>
              <div class="cal-cell has-appt">11</div><div class="cal-cell">12</div><div class="cal-cell has-appt">13</div><div class="cal-cell">14</div>
              <div class="cal-cell">15</div><div class="cal-cell">16</div><div class="cal-cell has-appt">17</div>
              <div class="cal-cell">18</div><div class="cal-cell">19</div><div class="cal-cell has-appt">20</div><div class="cal-cell">21</div>
            </div>
          </div>
          <div class="screen-caption">Calendário</div>
        </div>

        <!-- Ecrã 4: Odontograma -->
        <div class="screen-item">
          <div class="screen-header-bar">
            <span class="screen-header-title">Odontograma</span>
            <div class="screen-logo-small">nd</div>
          </div>
          <div class="screen-body">
            <div class="screen-label">Arcada Superior</div>
            <div class="odonto-grid">
              <div class="tooth">18</div><div class="tooth filled">17</div><div class="tooth">16</div>
              <div class="tooth treated">15</div><div class="tooth">14</div><div class="tooth">13</div>
              <div class="tooth">12</div><div class="tooth filled">11</div>
              <div class="tooth">21</div><div class="tooth">22</div><div class="tooth treated">23</div>
              <div class="tooth">24</div><div class="tooth">25</div><div class="tooth filled">26</div>
              <div class="tooth">27</div><div class="tooth">28</div>
            </div>
            <div class="screen-label" style="margin-top:10px;">Arcada Inferior</div>
            <div class="odonto-grid">
              <div class="tooth">48</div><div class="tooth">47</div><div class="tooth treated">46</div>
              <div class="tooth">45</div><div class="tooth filled">44</div><div class="tooth">43</div>
              <div class="tooth">42</div><div class="tooth">41</div>
              <div class="tooth">31</div><div class="tooth">32</div><div class="tooth">33</div>
              <div class="tooth filled">34</div><div class="tooth">35</div><div class="tooth treated">36</div>
              <div class="tooth">37</div><div class="tooth">38</div>
            </div>
            <div style="display:flex;gap:6px;margin-top:8px;flex-wrap:wrap;">
              <div style="display:flex;align-items:center;gap:3px;"><div style="width:8px;height:8px;border-radius:2px;background:rgba(142,68,173,0.35);"></div><span style="font-size:7px;color:rgba(255,255,255,0.5);">Cárie</span></div>
              <div style="display:flex;align-items:center;gap:3px;"><div style="width:8px;height:8px;border-radius:2px;background:rgba(0,200,120,0.2);"></div><span style="font-size:7px;color:rgba(255,255,255,0.5);">Tratado</span></div>
            </div>
          </div>
          <div class="screen-caption">Odontograma</div>
        </div>
        
        <!-- Ecrã 5: Pacientes -->
        <div class="screen-item">
          <div class="screen-header-bar">
            <span class="screen-header-title">Pacientes</span>
            <div class="screen-logo-small">nd</div>
          </div>
          <div class="screen-body">
            <div style="background:rgba(255,255,255,0.06);border-radius:8px;padding:6px 10px;margin-bottom:10px;font-size:9px;color:rgba(255,255,255,0.35);">🔍 Buscar paciente...</div>
            <div class="screen-list-item">
              <div>
                <div class="screen-item-name">Jéssica Andrade</div>
                <div class="screen-item-sub">Última visita: Jan/24</div>
              </div>
              <div class="screen-item-badge">→</div>
            </div>
            <div class="screen-list-item">
              <div>
                <div class="screen-item-name">Antônia Souza</div>
                <div class="screen-item-sub">Última visita: Fev/24</div>
              </div>
              <div class="screen-item-badge">→</div>
            </div>
            <div class="screen-list-item">
              <div>
                <div class="screen-item-name">Christian Firmino</div>
                <div class="screen-item-sub">Última visita: Mar/24</div>
              </div>
              <div class="screen-item-badge">→</div>
            </div>
          </div>
          <div class="screen-caption">Pacientes</div>
        </div>

        <!-- Ecrã 6: Orçamento -->
        <div class="screen-item">
          <div class="screen-header-bar">
            <span class="screen-header-title">Orçamentos</span>
            <div class="screen-logo-small">nd</div>
          </div>
          <div class="screen-body">
            <div class="screen-label">PENDENTES</div>
            <div class="screen-budget-item">
              <div class="screen-budget-title">Canal Radicular #36</div>
              <div class="screen-budget-val">R$ 1.200</div>
            </div>
            <div class="screen-budget-item">
              <div class="screen-budget-title">Clareamento Dental</div>
              <div class="screen-budget-val">R$ 800</div>
            </div>
            <div class="screen-label" style="margin-top:10px;">APROVADOS</div>
            <div class="screen-budget-item" style="border-left:2px solid #00c878;">
              <div class="screen-budget-title">Implante #46</div>
              <div class="screen-budget-val" style="color:#00c878;">R$ 4.500</div>
            </div>
            <div class="screen-budget-item" style="border-left:2px solid #00c878;">
              <div class="screen-budget-title">Facetas (6 un.)</div>
              <div class="screen-budget-val" style="color:#00c878;">R$ 7.200</div>
            </div>
          </div>
          <div class="screen-caption">Orçamentos</div>
        </div>

        <!-- Ecrã 7: Login -->
        <div class="screen-item">
          <div class="screen-header-bar" style="justify-content:center;">
            <span class="screen-header-title">newdental</span>
          </div>
          <div class="screen-body">
            <div style="text-align:center;margin-bottom:14px;">
              <div style="font-size:12px;font-weight:700;color:#fff;">Olá, desejamos<br/>um ótimo dia!</div>
            </div>
            <div style="border-bottom:1px solid rgba(142,68,173,0.4);padding:6px 0;margin-bottom:8px;">
              <div style="font-size:9px;color:rgba(255,255,255,0.4);">E-mail</div>
            </div>
            <div style="border-bottom:1px solid rgba(142,68,173,0.4);padding:6px 0;margin-bottom:14px;">
              <div style="font-size:9px;color:rgba(255,255,255,0.4);">Senha</div>
            </div>
            <div style="background:linear-gradient(135deg,var(--primary),var(--quaternary));border-radius:10px;padding:8px;text-align:center;margin-bottom:8px;">
              <div style="font-size:10px;font-weight:700;color:#fff;">Entrar</div>
            </div>
            <div style="text-align:center;font-size:8px;color:rgba(255,255,255,0.4);">Esqueci meu login, me ajude?</div>
          </div>
          <div class="screen-caption">Login</div>
        </div>
      </div>
    </section>

    <!-- PLANOS E PREÇOS -->
    <section class="pricing-section" id="planos">
      <v-container style="max-width: 1200px;">
        <div class="pricing-header reveal">
          <div class="section-badge">Planos & Valores</div>
          <h2 class="section-title">Escolha o plano<br/><span class="gradient-text">ideal para si</span></h2>
          <div class="pricing-toggle-row">
            <span :class="['toggle-label', !isAnnual ? 'active' : '']">Mensal</span>
            <button class="price-toggle" :class="{ annual: isAnnual }" @click="isAnnual = !isAnnual"></button>
            <span :class="['toggle-label', isAnnual ? 'active' : '']">Anual</span>
            <span class="save-badge">Poupe 30%</span>
          </div>
        </div>
        
        <v-row justify="center">
          <v-col cols="12" md="4" v-for="(plan, index) in plans" :key="index">
            <v-card class="pricing-card reveal" :class="[plan.featured ? 'featured' : '', `reveal-delay-${index + 1}`]" flat>
              <div v-if="plan.featured" class="popular-badge">⭐ Mais Popular</div>
              <div class="plan-name mb-8">{{ plan.name }}</div>
              <div class="plan-price">
                <sup>R$</sup>{{ isAnnual ? plan.priceAnnual : plan.priceMonthly }}
              </div>
              <div class="plan-price-period">por mês{{ isAnnual ? ' · cobrado anualmente' : '' }}</div>
              <div class="plan-divider"></div>
              
              <ul class="plan-features">
                <li v-for="(feat, fIndex) in plan.features" :key="fIndex">
                  <span v-if="feat.included" class="check">✓</span>
                  <span v-else class="cross">✗</span>
                  {{ feat.text }}
                </li>
              </ul>
              <button class="btn-plan" :class="plan.featured ? 'btn-plan-primary' : 'btn-plan-ghost'" @click="goToPlan(plan)">
                {{ plan.btnText }}
              </button>
            </v-card>
          </v-col>
        </v-row>

        <!-- Secção de QR Code e Download -->
        <div class="qr-section reveal mt-16">
          <div class="qr-download-block">
            <!-- Padrão Simulado de Código QR -->
            <div class="qr-code">
              <svg class="qr-pattern" viewBox="0 0 74 74" xmlns="http://www.w3.org/2000/svg" fill="#000">
                <rect x="2" y="2" width="20" height="20" rx="2" fill="#000"/>
                <rect x="4" y="4" width="16" height="16" rx="1" fill="#fff"/>
                <rect x="7" y="7" width="10" height="10" rx="1" fill="#8E44AD"/>
                <rect x="52" y="2" width="20" height="20" rx="2" fill="#000"/>
                <rect x="54" y="4" width="16" height="16" rx="1" fill="#fff"/>
                <rect x="57" y="7" width="10" height="10" rx="1" fill="#8E44AD"/>
                <rect x="2" y="52" width="20" height="20" rx="2" fill="#000"/>
                <rect x="4" y="54" width="16" height="16" rx="1" fill="#fff"/>
                <rect x="7" y="57" width="10" height="10" rx="1" fill="#8E44AD"/>
                <rect x="32" y="32" width="10" height="10" rx="2" fill="#8E44AD"/>
              </svg>
            </div>
            <div>
              <div class="qr-label">App Store</div>
              <div class="qr-sub">Disponível para iPhone e iPad</div>
              <a href="#" class="store-btn">
                <span class="store-btn-icon">🍎</span>
                <div>
                  <div style="font-size:9px;color:rgba(255,255,255,0.5);font-weight:400;">Descarregar na</div>
                  <div>App Store</div>
                </div>
              </a>
            </div>
          </div>

          <div class="qr-center-text d-none d-md-block">
            <div style="font-size:32px;margin-bottom:8px;">📱</div>
            <h3>Leia e descarregue</h3>
            <p>Disponível para<br/>iOS e Android</p>
          </div>

          <div class="qr-download-block" style="justify-content:flex-end;">
            <div style="text-align:right;">
              <div class="qr-label">Google Play</div>
              <div class="qr-sub">Disponível para Android</div>
              <a href="#" class="store-btn" style="justify-content:flex-end;">
                <span class="store-btn-icon">🤖</span>
                <div>
                  <div style="font-size:9px;color:rgba(255,255,255,0.5);font-weight:400;">Descarregar no</div>
                  <div>Google Play</div>
                </div>
              </a>
            </div>
            <div class="qr-code">
               <svg class="qr-pattern" viewBox="0 0 74 74" xmlns="http://www.w3.org/2000/svg" fill="#000">
                <rect x="2" y="2" width="20" height="20" rx="2" fill="#000"/>
                <rect x="4" y="4" width="16" height="16" rx="1" fill="#fff"/>
                <rect x="7" y="7" width="10" height="10" rx="1" fill="#8E44AD"/>
                <rect x="52" y="2" width="20" height="20" rx="2" fill="#000"/>
                <rect x="54" y="4" width="16" height="16" rx="1" fill="#fff"/>
                <rect x="57" y="7" width="10" height="10" rx="1" fill="#8E44AD"/>
                <rect x="2" y="52" width="20" height="20" rx="2" fill="#000"/>
                <rect x="4" y="54" width="16" height="16" rx="1" fill="#fff"/>
                <rect x="7" y="57" width="10" height="10" rx="1" fill="#8E44AD"/>
                <rect x="34" y="32" width="10" height="10" rx="2" fill="#8E44AD"/>
              </svg>
            </div>
          </div>
        </div>
      </v-container>
    </section>

    <!-- DEPOIMENTOS -->
    <section class="testimonials-section">
      <v-container style="max-width: 1200px;">
        <div class="reveal text-center">
          <div class="section-badge">Depoimentos</div>
          <h2 class="section-title">O que dizem<br/>os <span class="gradient-text">dentistas</span></h2>
        </div>
        
        <v-row class="mt-10">
          <v-col cols="12" md="4" v-for="(test, index) in testimonials" :key="index">
            <v-card class="testimonial-card reveal" :class="`reveal-delay-${index + 1}`" flat>
              <div class="stars">
                <span class="star" v-for="n in 5" :key="n">⭐</span>
              </div>
              <p class="testimonial-text">"{{ test.text }}"</p>
              <div class="testimonial-author">
                <div class="author-avatar" :style="{ background: test.color }">{{ test.initials }}</div>
                <div>
                  <div class="author-name">{{ test.name }}</div>
                  <div class="author-role">{{ test.role }}</div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- CALL TO ACTION -->
    <section class="cta-section">
      <v-container style="max-width: 1200px;">
        <v-card class="cta-card reveal" flat>
          <h2 class="cta-title">Pronto para transformar<br/>a sua clínica?</h2>
          <p class="cta-subtitle">Comece hoje com 14 dias gratuitos. Sem cartão de crédito. Cancele quando quiser.</p>
          <div class="cta-actions">
            <a href="#planos" class="btn-cta-white">🦷 Começar Grátis</a>
            <a href="#screenshots" class="btn-cta-ghost">▶ Ver a Aplicação</a>
          </div>
        </v-card>
      </v-container>
    </section>

    <!-- RODAPÉ -->
    <footer>
      <v-container style="max-width: 1200px;">
        <v-row class="mb-10">
          <v-col cols="12" md="4" class="footer-brand">
            <a href="#" class="logo">
              <div class="logo-icon">nd</div>
              <span class="logo-text">new<span>dental</span></span>
            </a>
            <p>O sistema de gestão odontológica mais moderno do mercado. Simples, rápido e feito para dentistas.</p>
          </v-col>
          <v-col cols="12" sm="4" md="2" class="footer-col">
            <h4>Produto</h4>
            <ul>
              <li><a href="#">Funcionalidades</a></li>
              <li><a href="#">Planos</a></li>
              <li><a href="#">Novidades</a></li>
              <li><a href="#">Roadmap</a></li>
            </ul>
          </v-col>
          <v-col cols="12" sm="4" md="3" class="footer-col">
            <h4>Suporte</h4>
            <ul>
              <li><a href="#">Centro de Ajuda</a></li>
              <li><a href="#">Tutoriais</a></li>
              <li><a href="#">Contacto</a></li>
              <li><a href="#">Estado do Sistema</a></li>
            </ul>
          </v-col>
          <v-col cols="12" sm="4" md="3" class="footer-col">
            <h4>Legal</h4>
            <ul>
              <li><a href="#">Termos de Utilização</a></li>
              <li><a href="#">Privacidade</a></li>
              <li><a href="#">RGPD</a></li>
              <li><a href="#">Cookies</a></li>
            </ul>
          </v-col>
        </v-row>
        <div class="footer-bottom">
          <span class="footer-copy">© 2026 New Dental. Todos os direitos reservados.</span>
          <div class="footer-socials">
            <a href="#" class="social-btn">in</a>
            <a href="#" class="social-btn">ig</a>
            <a href="#" class="social-btn">yt</a>
            <a href="#" class="social-btn">wa</a>
          </div>
        </div>
      </v-container>
    </footer>
  </div>
</template>

<script>
function getApiUrl() {
  const runtimeVariables = window.variables || {};
  return runtimeVariables.API || process.env.VUE_APP_API_URL || 'http://localhost:3001';
}

export default {
  name: "NewDentalLanding",
  data: () => ({
    scrolled: false,
    isAnnual: false,
    features: [
      { icon: '👥', title: 'Gestão de Pacientes', desc: 'Registo completo com histórico clínico, fotos, documentos e evolução do tratamento acessíveis em segundos.' },
      { icon: '📅', title: 'Agenda Inteligente', desc: 'Visualização semanal e mensal, notificações automáticas e controlo de horários por colaborador.' },
      { icon: '🦷', title: 'Odontograma Digital', desc: 'Mapeamento completo da arcada dentária com marcação de procedimentos, tratamentos e observações por dente.' },
      { icon: '😁', title: 'DSD — Digital Smile Design', desc: 'Planeamento estético digital integrado diretamente ao perfil do paciente para comunicar o resultado esperado.' },
      { icon: '💰', title: 'Orçamentos Profissionais', desc: 'Crie, envie e acompanhe orçamentos detalhados com valores por procedimento e facilidades de pagamento.' },
      { icon: '📝', title: 'Anamnese Completa', desc: 'Formulários personalizáveis de anamnese com histórico médico, alergias e condições sistémicas do paciente.' }
    ],
    marketingTags: ['iOS & Android', 'Nuvem', 'RGPD', 'Multiusuário'],
    marketingList: [
      'Acesso offline com sincronização automática',
      'Cópia de segurança na nuvem com segurança total',
      'Suporte especializado em até 2 horas',
      'Atualizações constantes sem custo adicional',
      'Conformidade com o RGPD e as ordens locais'
    ],
    plans: [],
    testimonials: [
      { initials: 'DR', color: 'linear-gradient(135deg,#8E44AD,#5c1a7a)', name: 'Dra. Renata Castilho', role: 'Ortodontista • São Paulo', text: 'O New Dental transformou a minha clínica. Antes usava papel e folha de cálculo — hoje está tudo na aplicação, do odontograma ao orçamento. A minha equipa adotou-o num 1 dia.' },
      { initials: 'FM', color: 'linear-gradient(135deg,#862AAC,#4a1066)', name: 'Dr. Filipe Moreira', role: 'Implantodontista • Porto', text: 'A agenda integrada com os pacientes é incrível. Recebo lembretes automáticos, os meus pacientes confirmam pelo WhatsApp e a taxa de faltas caiu 40%.' },
      { initials: 'PA', color: 'linear-gradient(135deg,#9D4EBE,#6b1f8c)', name: 'Dra. Patrícia Alves', role: 'Odontologia Estética • Lisboa', text: 'O DSD e o odontograma digital são diferenciais absurdos. Consigo mostrar o planeamento ao paciente na hora, isto aumentou muito a aprovação dos meus orçamentos.' }
    ]
  }),

  async created() {
    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/account/plans`);
      const json = await res.json();
      const raw = json.data ?? json;
      this.plans = raw.map((p, index) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        priceMonthly: Math.floor(p.value).toString(),
        priceAnnual: Math.floor(p.value * 0.7).toString(),
        featured: index === 1,
        btnText: index === 1 ? 'Assinar agora' : 'Começar agora',
        features: this.buildFeatures(p),
      }));
    } catch (e) {
      // keep empty array on error
    }
  },

  mounted() {
    this.initScrollReveal();
    window.addEventListener('scroll', this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },

  methods: {
    buildFeatures(p) {
      const features = [];
      const users = p.maxUsers;
      const patients = p.maxPatients;
      features.push({ text: users === null ? 'Dentistas ilimitados' : users === 1 ? '1 dentista' : `Até ${users} dentistas`, included: true });
      features.push({ text: patients === null ? 'Pacientes ilimitados' : `Até ${patients} pacientes`, included: true });
      features.push({ text: 'Agenda completa', included: true });
      features.push({ text: 'Odontograma', included: true });
      features.push({ text: 'Anamnese', included: true });
      features.push({ text: 'DSD', included: users === null || (users !== null && users > 1) });
      features.push({ text: 'Múltiplos colaboradores', included: users === null || (users !== null && users > 1) });
      features.push({ text: 'Relatórios avançados', included: users === null });
      return features;
    },

    goToPlan(plan) {
      this.$router.push({
        path: '/cadastro',
        query: { planId: plan.id, billing: this.isAnnual ? 'annual' : 'monthly' }
      });
    },
    handleScroll() {
      this.scrolled = window.scrollY > 60;
    },
    initScrollReveal() {
      // Configuramos o IntersectionObserver para animar os blocos que entram na view
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      }, { threshold: 0.1 });
      
      this.$el.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    }
  }
};
</script>

<style lang="scss" scoped>
/* Variáveis mapeadas para o padrão SCSS a partir do :root original */
$primary: #8E44AD;
$secondary: #FFFFFF;
$tertiary: #9D4EBE;
$quaternary: #862AAC;
$dark: #0D0618;
$dark2: #160D22;
$card-bg: rgba(142, 68, 173, 0.08);
$glass: rgba(255, 255, 255, 0.06);
$glass-border: rgba(255, 255, 255, 0.12);

@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Syne:wght@700;800&display=swap');

/* ── BASE DA PÁGINA ── */
.landing-wrapper {
  font-family: 'Plus Jakarta Sans', sans-serif;
  background: $dark;
  color: #fff;
  overflow-x: hidden;
  position: relative;
  min-height: 100vh;
}

.noise-bg {
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
  pointer-events: none;
  z-index: 0;
  opacity: 0.5;
}

.syne { font-family: 'Syne', sans-serif; }
.z-index-1 { z-index: 1; }

.section-badge {
  display: inline-block;
  background: rgba(142, 68, 173, 0.15);
  border: 1px solid rgba(142, 68, 173, 0.3);
  color: #c77dff; font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 1px; padding: 5px 14px; border-radius: 100px; margin-bottom: 16px;
}

.gradient-text {
  background: linear-gradient(135deg, #c77dff, $quaternary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* ── BARRA DE NAVEGAÇÃO ── */
.navbar {
  position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
  padding: 18px 0; background: rgba(13, 6, 24, 0.7);
  backdrop-filter: blur(20px); border-bottom: 1px solid $glass-border;
  transition: all 0.3s ease;

  &.navbar-scrolled {
    background: rgba(13, 6, 24, 0.95);
    padding: 12px 0;
  }

  .navbar-inner {
    max-width: 1200px; margin: 0 auto; padding: 0 32px;
    display: flex; align-items: center; justify-content: space-between;
  }
}

.logo {
  display: flex; align-items: center; gap: 10px; text-decoration: none;
  
  .logo-icon {
    width: 38px; height: 38px; background: linear-gradient(135deg, $primary, $tertiary);
    border-radius: 10px; display: flex; align-items: center; justify-content: center;
    font-weight: 800; font-size: 14px; color: #fff; box-shadow: 0 4px 20px rgba(142, 68, 173, 0.5);
  }
  .logo-text { font-family: 'Syne', sans-serif; font-size: 20px; font-weight: 700; color: #fff; span { color: $tertiary; } }
}

.nav-links {
  display: flex; gap: 36px; list-style: none; padding-left: 0;
  a { color: rgba(255, 255, 255, 0.7); text-decoration: none; font-size: 14px; font-weight: 500; transition: color 0.2s; &:hover { color: #fff; } }
  @media (max-width: 900px) { display: none; }
}

.nav-cta {
  background: linear-gradient(135deg, $primary, $quaternary); color: #fff !important; padding: 10px 24px;
  border-radius: 100px; font-weight: 600; font-size: 14px; text-decoration: none;
  box-shadow: 0 4px 20px rgba(142, 68, 173, 0.4); transition: all 0.3s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 8px 30px rgba(142, 68, 173, 0.6); }
}

/* ── HERO ── */
.hero {
  min-height: 100vh; display: flex; align-items: center; position: relative; overflow: hidden; padding-top: 80px;

  .hero-bg-orb {
    position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none;
    &.orb1 { width: 600px; height: 600px; background: radial-gradient(circle, rgba(142, 68, 173, 0.35) 0%, transparent 70%); top: -100px; left: -200px; }
    &.orb2 { width: 500px; height: 500px; background: radial-gradient(circle, rgba(157, 78, 190, 0.25) 0%, transparent 70%); bottom: -100px; right: -150px; }
    &.orb3 { width: 300px; height: 300px; background: radial-gradient(circle, rgba(134, 42, 172, 0.2) 0%, transparent 70%); top: 40%; left: 50%; transform: translateX(-50%); }
  }

  .hero-badge {
    display: inline-flex; align-items: center; gap: 8px; background: rgba(142, 68, 173, 0.15);
    border: 1px solid rgba(142, 68, 173, 0.35); border-radius: 100px; padding: 6px 16px;
    font-size: 12px; font-weight: 600; color: #c77dff; text-transform: uppercase; margin-bottom: 24px;
    .hero-badge-dot { width: 6px; height: 6px; background: #c77dff; border-radius: 50%; animation: pulse-dot 1.5s ease-in-out infinite; }
  }

  .hero-title { font-family: 'Syne', sans-serif; font-size: clamp(42px, 5vw, 68px); font-weight: 800; line-height: 1.05; letter-spacing: -2px; margin-bottom: 20px; }
  .hero-subtitle { font-size: 17px; line-height: 1.7; color: rgba(255, 255, 255, 0.65); max-width: 480px; margin-bottom: 40px; }
  
  .hero-stats { display: flex; gap: 32px; margin-top: 48px; .hero-stat-item { display: flex; flex-direction: column; } .hero-stat-number { font-family: 'Syne', sans-serif; font-size: 28px; font-weight: 800; background: linear-gradient(135deg, #fff, #c77dff); -webkit-background-clip: text; -webkit-text-fill-color: transparent; } .hero-stat-label { font-size: 12px; color: rgba(255, 255, 255, 0.5); font-weight: 500; margin-top: 2px; } }
}

.btn-primary { background: linear-gradient(135deg, $primary, $quaternary); color: #fff; padding: 16px 34px; border-radius: 100px; font-weight: 700; font-size: 15px; text-decoration: none; display: inline-flex; transition: all 0.3s; box-shadow: 0 8px 30px rgba(142, 68, 173, 0.5); margin-right: 16px; margin-bottom: 16px; &:hover { transform: translateY(-2px); box-shadow: 0 16px 40px rgba(142, 68, 173, 0.7); } }
.btn-secondary { background: transparent; color: rgba(255, 255, 255, 0.8); padding: 16px 34px; border-radius: 100px; font-weight: 600; font-size: 15px; text-decoration: none; border: 1px solid rgba(255, 255, 255, 0.2); display: inline-flex; transition: all 0.3s; margin-bottom: 16px; &:hover { background: rgba(255, 255, 255, 0.05); color: #fff; } }

@keyframes pulse-dot { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.7); } }

/* ── MOCKUP DO TELEMÓVEL E CARTÕES FLUTUANTES ── */
.phone-mockup { width: 240px; background: linear-gradient(160deg, #1a0b2e 0%, #0f0620 100%); border-radius: 40px; border: 2px solid rgba(142, 68, 173, 0.4); overflow: hidden; box-shadow: 0 40px 100px rgba(0, 0, 0, 0.6); position: relative; z-index: 2; animation: float 4s ease-in-out infinite; }
@keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-12px); } }
.phone-notch { width: 80px; height: 24px; background: #0f0620; border-radius: 0 0 16px 16px; margin: 0 auto; position: relative; z-index: 3; }
.phone-screen { padding: 8px 12px 20px; }
.phone-header-bar { background: linear-gradient(135deg, $primary, $quaternary); border-radius: 16px; padding: 14px; margin-bottom: 10px; display: flex; justify-content: space-between;}
.phone-header-logo { font-family: 'Syne', sans-serif; font-size: 11px; font-weight: 700; color: rgba(255, 255, 255, 0.8); margin-bottom: 6px; }
.phone-avatar { width: 40px; height: 40px; background: rgba(255, 255, 255, 0.2); border-radius: 50%; }
.phone-user-name { font-size: 13px; font-weight: 700; color: #fff; }
.phone-user-sub { font-size: 9px; color: rgba(255, 255, 255, 0.7); }
.phone-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 8px; }
.phone-card { background: rgba(142, 68, 173, 0.15); border: 1px solid rgba(142, 68, 173, 0.25); border-radius: 12px; padding: 10px 8px; display: flex; flex-direction: column; align-items: center; gap: 4px; }
.phone-card-icon { width: 24px; height: 24px; background: rgba(142, 68, 173, 0.3); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 12px; }
.phone-card-label { font-size: 8px; color: rgba(255, 255, 255, 0.7); font-weight: 600; text-transform: uppercase; }
.phone-appt { background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 8px 10px; display: flex; align-items: center; justify-content: space-between; margin-bottom: 5px; }
.phone-appt-name { font-size: 9px; font-weight: 600; color: #fff; }
.phone-appt-time { font-size: 8px; color: rgba(255, 255, 255, 0.5); }
.phone-appt-dot { width: 6px; height: 6px; background: #c77dff; border-radius: 50%; }
.phone-nav { background: rgba(142, 68, 173, 0.15); border-radius: 12px; padding: 8px; display: flex; justify-content: space-around; margin-top: 6px; }
.phone-nav-item { font-size: 11px; color: rgba(255, 255, 255, 0.5); &.active { background: rgba(142, 68, 173, 0.2); border-radius: 8px; } }
.screen-logo-small { width: 26px; height: 26px; background: rgba(255, 255, 255, 0.2); border-radius: 7px; display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 800; color: #fff; }

.float-card { position: absolute; background: rgba(22, 13, 34, 0.9); backdrop-filter: blur(16px); border: 1px solid rgba(142, 68, 173, 0.25); border-radius: 16px; padding: 12px 16px; min-width: 140px; }
.float-card-1 { top: 60px; left: -80px; animation: float-card1 5s ease-in-out infinite; }
.float-card-2 { bottom: 80px; right: -70px; animation: float-card2 6s ease-in-out infinite; }
.float-card-3 { top: 50%; right: -90px; transform: translateY(-50%); animation: float-card3 4.5s ease-in-out infinite; }
@keyframes float-card1 { 0%, 100% { transform: translateY(0) rotate(-2deg); } 50% { transform: translateY(-8px) rotate(0deg); } }
@keyframes float-card2 { 0%, 100% { transform: translateY(0) rotate(2deg); } 50% { transform: translateY(-10px) rotate(0deg); } }
@keyframes float-card3 { 0%, 100% { transform: translateY(-50%) rotate(1deg); } 50% { transform: translateY(calc(-50% - 8px)) rotate(-1deg); } }
.float-card-icon { font-size: 18px; margin-bottom: 4px; }
.float-card-label { font-size: 10px; color: rgba(255, 255, 255, 0.5); font-weight: 500; }
.float-card-value { font-family: 'Syne', sans-serif; font-size: 18px; font-weight: 800; color: #fff; }
.float-card-sub { font-size: 10px; color: #c77dff; font-weight: 600; }

/* ── FUNCIONALIDADES ── */
.features-section { padding: 120px 0; position: relative; z-index: 1;}
.features-header { text-align: center; margin-bottom: 72px; }
.section-title { font-family: 'Syne', sans-serif; font-size: clamp(32px, 4vw, 52px); font-weight: 800; line-height: 1.1; letter-spacing: -1.5px; }
.features-subtitle { color: rgba(255, 255, 255, 0.6); font-size: 17px; line-height: 1.7; max-width: 560px; margin: 16px auto 0; }

.feature-card {
  background: $glass !important; border: 1px solid $glass-border !important; border-radius: 24px !important; padding: 32px; transition: all 0.3s ease; position: relative; overflow: hidden;
  &:hover { transform: translateY(-4px); border-color: rgba(142, 68, 173, 0.4) !important; box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3) !important; }
  .feature-icon { width: 52px; height: 52px; background: linear-gradient(135deg, rgba(142, 68, 173, 0.3), rgba(134, 42, 172, 0.15)); border: 1px solid rgba(142, 68, 173, 0.3); border-radius: 16px; display: flex; align-items: center; justify-content: center; font-size: 22px; margin-bottom: 20px; }
  .feature-title { font-family: 'Syne', sans-serif; font-size: 18px; font-weight: 700; margin-bottom: 10px; color: #fff; }
  .feature-desc { font-size: 14px; line-height: 1.7; color: rgba(255, 255, 255, 0.55); }
}

/* ── MARKETING E GRÁFICOS ── */
.marketing-section { padding: 100px 0; background: linear-gradient(180deg, transparent 0%, rgba(142, 68, 173, 0.06) 50%, transparent 100%); }
.marketing-visual { position: relative; }
.mkt-big-number { font-family: 'Syne', sans-serif; font-size: clamp(80px, 10vw, 140px); font-weight: 800; line-height: 1; background: linear-gradient(135deg, rgba(142, 68, 173, 0.3), rgba(157, 78, 190, 0.1)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; position: absolute; top: -20px; left: -10px; pointer-events: none; user-select: none; }
.mkt-card-stack { position: relative; height: 380px; }
.mkt-card { position: absolute; background: rgba(22, 13, 34, 0.95); border: 1px solid rgba(142, 68, 173, 0.25); border-radius: 20px; padding: 24px; }
.mkt-card-main { width: 100%; top: 30px; left: 0; z-index: 3; }
.mkt-card-back1 { width: 92%; top: 15px; left: 4%; z-index: 2; opacity: 0.5; }
.mkt-card-back2 { width: 84%; top: 0; left: 8%; z-index: 1; opacity: 0.25; }
.mkt-chart-bar { display: flex; align-items: flex-end; gap: 8px; height: 80px; margin: 16px 0; }
.bar { flex: 1; border-radius: 6px 6px 0 0; background: linear-gradient(180deg, $primary, $quaternary); opacity: 0.7; transition: opacity 0.2s; &:hover, &.active { opacity: 1; } }
.mkt-content p { font-size: 15px; line-height: 1.8; color: rgba(255, 255, 255, 0.65); margin-bottom: 16px; }
.mkt-list { list-style: none; display: flex; flex-direction: column; gap: 12px; margin-bottom: 32px; padding-left: 0; }
.mkt-list li { display: flex; align-items: center; gap: 12px; font-size: 15px; color: rgba(255, 255, 255, 0.75); }
.mkt-list li::before { content: ''; width: 6px; height: 6px; min-width: 6px; background: $primary; border-radius: 50%; box-shadow: 0 0 8px rgba(142, 68, 173, 0.8); }
.mkt-tag-row { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 8px; }
.mkt-tag { background: rgba(142, 68, 173, 0.12); border: 1px solid rgba(142, 68, 173, 0.25); border-radius: 100px; padding: 6px 14px; font-size: 12px; font-weight: 600; color: #c77dff; }

/* ── CAPTURAS DE ECRÃ ── */
.screenshots-section { padding: 120px 0; overflow: hidden; }
.screenshots-header { text-align: center; margin-bottom: 64px; }
.screens-track { display: flex; gap: 20px; overflow-x: auto; padding: 20px 32px 40px; scrollbar-width: none; -ms-overflow-style: none; max-width: 1200px; margin: 0 auto; &::-webkit-scrollbar { display: none; } }
.screen-item { min-width: 180px; background: linear-gradient(160deg, #1a0b2e 0%, #0f0620 100%); border-radius: 32px; border: 1.5px solid rgba(142, 68, 173, 0.3); overflow: hidden; transition: all 0.3s ease; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4); flex-shrink: 0; &:hover { transform: translateY(-8px) scale(1.02); border-color: rgba(142, 68, 173, 0.6); box-shadow: 0 40px 80px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(142, 68, 173, 0.2); } }
.screen-header-bar { background: linear-gradient(135deg, $primary, $quaternary); height: 60px; display: flex; align-items: center; justify-content: space-between; padding: 0 14px; }
.screen-header-title { font-size: 11px; font-weight: 700; color: #fff; }
.screen-body { padding: 14px; }
.screen-label { font-size: 9px; font-weight: 700; color: rgba(255, 255, 255, 0.5); text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 10px; }
.screen-list-item { background: rgba(142, 68, 173, 0.1); border: 1px solid rgba(142, 68, 173, 0.15); border-radius: 10px; padding: 8px 10px; margin-bottom: 6px; display: flex; align-items: center; justify-content: space-between; }
.screen-item-name { font-size: 9px; font-weight: 600; color: #fff; }
.screen-item-sub { font-size: 8px; color: rgba(255, 255, 255, 0.4); }
.screen-item-badge { width: 16px; height: 16px; background: $primary; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 8px; }
.screen-calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; margin-top: 8px; }
.cal-cell { aspect-ratio: 1; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 7px; color: rgba(255, 255, 255, 0.5); &.today { background: $primary; color: #fff; font-weight: 700; } &.has-appt { background: rgba(142, 68, 173, 0.2); color: rgba(255, 255, 255, 0.8); } }
.odonto-grid { display: grid; grid-template-columns: repeat(8, 1fr); gap: 2px; margin-top: 8px; }
.tooth { aspect-ratio: 1; border-radius: 4px; background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.08); display: flex; align-items: center; justify-content: center; font-size: 5px; color: rgba(255, 255, 255, 0.3); &.filled { background: rgba(142, 68, 173, 0.35); border-color: rgba(142, 68, 173, 0.4); } &.treated { background: rgba(0, 200, 120, 0.2); border-color: rgba(0, 200, 120, 0.3); color: rgba(0, 200, 120, 0.8); } }
.screen-budget-item { background: rgba(255, 255, 255, 0.04); border-radius: 8px; padding: 7px 9px; margin-bottom: 5px; }
.screen-budget-title { font-size: 9px; font-weight: 600; color: rgba(255, 255, 255, 0.8); }
.screen-budget-val { font-size: 11px; font-weight: 700; color: #c77dff; margin-top: 1px; }
.tab-row { display: flex; gap: 4px; margin-bottom: 10px; }
.tab-btn { flex: 1; background: rgba(255, 255, 255, 0.05); border-radius: 8px; padding: 5px 0; font-size: 8px; font-weight: 600; color: rgba(255, 255, 255, 0.4); text-align: center; &.active { background: $primary; color: #fff; } }
.screen-caption { text-align: center; padding: 12px; font-size: 11px; font-weight: 600; color: rgba(255, 255, 255, 0.4); }

/* ── PREÇOS E CÓDIGOS QR ── */
.pricing-section { padding: 120px 0; position: relative; z-index: 1;}
.pricing-header { text-align: center; margin-bottom: 64px; }
.pricing-toggle-row { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 24px; }
.toggle-label { font-size: 14px; font-weight: 600; color: rgba(255, 255, 255, 0.6); &.active { color: #fff; } }
.price-toggle { width: 48px; height: 26px; background: $primary; border-radius: 100px; border: none; cursor: pointer; position: relative; transition: background 0.3s; }
.price-toggle::after { content: ''; position: absolute; width: 20px; height: 20px; background: #fff; border-radius: 50%; top: 3px; left: 3px; transition: transform 0.3s; }
.price-toggle.annual::after { transform: translateX(22px); }
.save-badge { background: linear-gradient(135deg, #00c878, #00a061); color: #fff; font-size: 10px; font-weight: 700; padding: 3px 10px; border-radius: 100px; text-transform: uppercase; }

.pricing-card {
  background: $glass !important; border: 1px solid $glass-border !important; border-radius: 28px !important; padding: 36px 28px; position: relative; transition: all 0.3s ease; height: 100%;
  &:hover { transform: translateY(-4px); box-shadow: 0 30px 60px rgba(0, 0, 0, 0.3); }
  &.featured { background: linear-gradient(160deg, rgba(142, 68, 173, 0.2), rgba(134, 42, 172, 0.08)) !important; border-color: rgba(142, 68, 173, 0.5) !important; box-shadow: 0 0 0 1px rgba(142, 68, 173, 0.15) !important; }
}
.popular-badge { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: linear-gradient(135deg, $primary, $quaternary); color: #fff; font-size: 11px; font-weight: 700; padding: 4px 18px; border-radius: 100px; white-space: nowrap; }
.plan-name { font-size: 13px; font-weight: 700; text-transform: uppercase; color: rgba(255, 255, 255, 0.5); margin-bottom: 6px; }
.plan-price { font-family: 'Syne', sans-serif; font-size: 48px; font-weight: 800; line-height: 1; color: #fff; margin-bottom: 4px; sup { font-size: 22px; vertical-align: top; margin-top: 8px; } }
.plan-price-period { font-size: 13px; color: rgba(255, 255, 255, 0.4); margin-bottom: 24px; }
.plan-divider { height: 1px; background: $glass-border; margin: 24px 0; }
.plan-features { list-style: none; display: flex; flex-direction: column; gap: 12px; margin-bottom: 28px; padding-left: 0;}
.plan-features li { display: flex; align-items: center; gap: 10px; font-size: 13px; color: rgba(255, 255, 255, 0.7); }
.check { width: 18px; height: 18px; min-width: 18px; background: rgba(142, 68, 173, 0.2); border: 1px solid rgba(142, 68, 173, 0.35); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #c77dff; }
.cross { width: 18px; height: 18px; min-width: 18px; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 9px; color: rgba(255, 255, 255, 0.25); }

.btn-plan { width: 100%; padding: 14px; border-radius: 14px; font-weight: 700; font-size: 14px; border: none; cursor: pointer; transition: all 0.3s ease; }
.btn-plan-ghost { background: rgba(255, 255, 255, 0.06); color: rgba(255, 255, 255, 0.7); border: 1px solid rgba(255, 255, 255, 0.12); &:hover { background: rgba(255, 255, 255, 0.1); color: #fff; } }
.btn-plan-primary { background: linear-gradient(135deg, $primary, $quaternary); color: #fff; box-shadow: 0 8px 24px rgba(142, 68, 173, 0.4); &:hover { transform: translateY(-1px); } }

/* QR Codes */
.qr-section { background: $glass; border: 1px solid $glass-border; border-radius: 28px; padding: 40px; display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; }
.qr-download-block { display: flex; align-items: center; gap: 20px; }
.qr-code { width: 90px; height: 90px; background: #fff; border-radius: 12px; padding: 8px; flex-shrink: 0; }
.qr-pattern { width: 100%; height: 100%; }
.qr-label { font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 800; color: #fff; margin-bottom: 4px; }
.qr-sub { font-size: 12px; color: rgba(255, 255, 255, 0.5); margin-bottom: 12px; }
.store-btn { display: inline-flex; align-items: center; gap: 8px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 10px; padding: 8px 14px; color: #fff; font-size: 12px; font-weight: 600; text-decoration: none; transition: all 0.2s; margin-bottom: 8px; &:hover { background: rgba(255, 255, 255, 0.12); border-color: rgba(255, 255, 255, 0.25); } }
.store-btn-icon { font-size: 16px; }
.qr-center-text { text-align: center; h3 { font-family: 'Syne', sans-serif; font-size: 20px; font-weight: 800; margin-bottom: 8px; } p { font-size: 13px; color: rgba(255, 255, 255, 0.5); } }

/* ── DEPOIMENTOS ── */
.testimonials-section { padding: 100px 0; background: linear-gradient(180deg, transparent 0%, rgba(142, 68, 173, 0.04) 50%, transparent 100%); position: relative; z-index: 1;}
.testimonial-card { background: $glass !important; border: 1px solid $glass-border !important; border-radius: 20px !important; padding: 28px; transition: all 0.3s; height: 100%; &:hover { transform: translateY(-4px); border-color: rgba(142, 68, 173, 0.3) !important; } }
.stars { display: flex; gap: 3px; margin-bottom: 16px; .star { font-size: 14px; } }
.testimonial-text { font-size: 14px; line-height: 1.75; color: rgba(255, 255, 255, 0.65); margin-bottom: 20px; }
.testimonial-author { display: flex; align-items: center; gap: 12px; }
.author-avatar { width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; flex-shrink: 0; }
.author-name { font-size: 13px; font-weight: 700; color: #fff; }
.author-role { font-size: 11px; color: rgba(255, 255, 255, 0.4); }

/* ── CTA E RODAPÉ ── */
.cta-section { padding: 100px 0; }
.cta-card { background: linear-gradient(135deg, $primary 0%, $quaternary 50%, #5c1a7a 100%) !important; border-radius: 32px !important; padding: 72px 64px; text-align: center; position: relative; overflow: hidden; }
.cta-card::before { content: ''; position: absolute; width: 400px; height: 400px; background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%); top: -100px; right: -100px; border-radius: 50%; }
.cta-card::after { content: ''; position: absolute; width: 300px; height: 300px; background: radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%); bottom: -80px; left: -80px; border-radius: 50%; }
.cta-title { font-family: 'Syne', sans-serif; font-size: clamp(32px, 4vw, 54px); font-weight: 800; color: #fff; margin-bottom: 16px; position: relative; z-index: 1; letter-spacing: -1.5px; }
.cta-subtitle { font-size: 17px; color: rgba(255, 255, 255, 0.75); max-width: 520px; margin: 0 auto 40px; line-height: 1.7; position: relative; z-index: 1; }
.cta-actions { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; }
.btn-cta-white { background: #fff; color: $primary; padding: 16px 36px; border-radius: 100px; font-weight: 800; font-size: 15px; text-decoration: none; display: inline-flex; transition: all 0.3s ease; &:hover { transform: translateY(-2px); box-shadow: 0 16px 40px rgba(0, 0, 0, 0.3); } }
.btn-cta-ghost { background: transparent; color: rgba(255, 255, 255, 0.85); padding: 16px 36px; border-radius: 100px; font-weight: 600; font-size: 15px; text-decoration: none; border: 2px solid rgba(255, 255, 255, 0.3); display: inline-flex; transition: all 0.3s ease; &:hover { background: rgba(255, 255, 255, 0.1); border-color: rgba(255, 255, 255, 0.6); color: #fff; } }

footer { border-top: 1px solid $glass-border; padding: 64px 0 32px; }
.footer-brand p { font-size: 14px; color: rgba(255, 255, 255, 0.5); line-height: 1.7; margin-top: 16px; max-width: 260px; }
.footer-col h4 { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: rgba(255, 255, 255, 0.5); margin-bottom: 16px; }
.footer-col ul { list-style: none; display: flex; flex-direction: column; gap: 10px; padding-left: 0; }
.footer-col a { color: rgba(255, 255, 255, 0.6); text-decoration: none; font-size: 14px; transition: color 0.2s; &:hover { color: #fff; } }
.footer-bottom { border-top: 1px solid $glass-border; padding-top: 28px; display: flex; align-items: center; justify-content: space-between; }
.footer-copy { font-size: 13px; color: rgba(255, 255, 255, 0.35); }
.footer-socials { display: flex; gap: 12px; }
.social-btn { width: 36px; height: 36px; background: $glass; border: 1px solid $glass-border; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: rgba(255, 255, 255, 0.5); text-decoration: none; font-size: 14px; transition: all 0.2s; &:hover { background: rgba(142, 68, 173, 0.15); border-color: rgba(142, 68, 173, 0.3); color: #c77dff; } }

/* ── ANIMAÇÕES ── */
.reveal { opacity: 0; transform: translateY(30px); transition: opacity 0.7s ease, transform 0.7s ease; }
.reveal.visible { opacity: 1; transform: translateY(0); }
.reveal-delay-1 { transition-delay: 0.1s; }
.reveal-delay-2 { transition-delay: 0.2s; }
.reveal-delay-3 { transition-delay: 0.3s; }
</style>
